'use client';

import { memo, useEffect, useState } from 'react';

import { Check, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import type {
  CharacterConfig,
  Ethnicity,
  Gender,
  SkinTone,
  StyleType,
} from './types';

function GenderIcon({
  gender,
  className,
}: {
  gender: string;
  className?: string;
}) {
  if (gender === 'female') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="12" cy="9" r="6" />
        <line x1="12" y1="15" x2="12" y2="22" />
        <line x1="9" y1="19" x2="15" y2="19" />
      </svg>
    );
  }
  if (gender === 'male') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        <circle cx="10" cy="14" r="6" />
        <line x1="22" y1="2" x2="15" y2="9" />
        <polyline points="16 2 22 2 22 8" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="5" />
      <line x1="11" y1="16" x2="11" y2="22" />
      <line x1="8" y1="19" x2="14" y2="19" />
      <line x1="19" y1="2" x2="13" y2="8" />
      <polyline points="14 2 19 2 19 7" />
      <line x1="19" y1="14" x2="13" y2="8" />
    </svg>
  );
}

interface Step2EthnicityProps {
  config: CharacterConfig;
  updateConfig: (updates: Partial<CharacterConfig>) => void;
  onBack: () => void;
  onNext: () => void;
}

const ethnicities: Ethnicity[] = [
  'african',
  'european',
  'asian',
  'latina',
  'middle-eastern',
  'scandinavian',
];

function getEthnicityImage(id: string, style: StyleType, gender: Gender) {
  // realistic/female not in card-images, fall back to existing folder
  if (style === 'realistic' && gender === 'female') {
    return `/images/character-creator/2nd-step/${style}/female/${id}.avif`;
  }
  return `/images/character-creator/card-images/2nd-step/${style}/${gender}/${id}/1.avif`;
}

const skinTones: { id: SkinTone; color: string }[] = [
  { id: 'ivory', color: '#f5e6d3' },
  { id: 'light', color: '#e8b896' },
  { id: 'tan', color: '#c68642' },
  { id: 'caramel', color: '#9d6b3b' },
  { id: 'brown', color: '#7a4f2e' },
  { id: 'deep', color: '#5d3a1a' },
  { id: 'deep-dark', color: '#3d2817' },
];

// Furry species options
const furrySpeciesIds: string[] = [
  'wolf',
  'fox',
  'cat',
  'dog',
  'dragon',
  'rabbit',
  'deer',
  'tiger',
  'lion',
  'bear',
  'otter',
  'shark',
];

function getSpeciesImage(id: string, gender: Gender) {
  return `/images/character-creator/card-images/2nd-step/furry-species/${gender}/${id}/1.avif`;
}

const furColors: { id: string; color: string }[] = [
  { id: 'white', color: '#f0f0f0' },
  { id: 'grey', color: '#808080' },
  { id: 'black', color: '#2a2a2a' },
  { id: 'brown', color: '#8B4513' },
  { id: 'orange', color: '#FF8C00' },
  { id: 'red', color: '#B22222' },
  { id: 'cream', color: '#FFFDD0' },
  { id: 'golden', color: '#DAA520' },
  { id: 'silver', color: '#C0C0C0' },
  { id: 'blue', color: '#4169E1' },
  { id: 'purple', color: '#8B008B' },
  { id: 'pink', color: '#FF69B4' },
];

export const Step2Ethnicity = memo(function Step2Ethnicity({
  config,
  updateConfig,
  onBack,
  onNext,
}: Step2EthnicityProps) {
  const { t } = useTranslation('common');
  const isFurry = config.style === 'furry';

  // Shared between the furry and standard branches (both need age).
  const ageSlider = (
    <div className="max-w-full">
      <div className="mb-1.5 flex items-center justify-between sm:mb-2">
        <span className="text-2xl font-bold text-white sm:text-3xl md:text-4xl">
          {config.age}
        </span>
        <div className="flex items-center gap-1 rounded-full bg-red-600 px-2 py-1 sm:gap-1.5 sm:px-3 sm:py-1.5">
          <Check className="h-2.5 w-2.5 text-white sm:h-3 sm:w-3" />
          <span className="text-[9px] font-semibold text-white sm:text-[10px] md:text-xs">
            <Trans i18nKey="common:characterCreator.verifiedBadge" />
          </span>
        </div>
      </div>
      <input
        type="range"
        min="18"
        max="55"
        value={config.age}
        onChange={(e) => updateConfig({ age: parseInt(e.target.value) })}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-gradient-to-r from-red-600 to-pink-600 sm:h-2 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg sm:[&::-webkit-slider-thumb]:h-5 sm:[&::-webkit-slider-thumb]:w-5"
      />
      <div className="mt-0.5 flex justify-between text-[10px] text-gray-400 sm:mt-1 sm:text-xs">
        <span>18</span>
        <span className="font-medium text-white">{config.age}</span>
        <span>55+</span>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl px-2 sm:px-4 lg:px-0">
      {/* Title */}
      <div className="mb-2 text-center sm:mb-3">
        <h1 className="mb-0.5 text-lg font-bold text-white sm:mb-1 sm:text-xl md:text-2xl">
          <Trans i18nKey="common:characterCreator.step1Title" />
        </h1>
        <p className="bg-gradient-to-r from-red-400 via-pink-400 to-pink-600 bg-clip-text text-[10px] font-semibold text-transparent sm:text-xs md:text-sm">
          <Trans
            i18nKey={
              isFurry
                ? 'common:characterCreator.step2SpeciesFur'
                : 'common:characterCreator.step2EthnicityAge'
            }
          />
        </p>
      </div>

      {/* Gender Display */}
      <div className="mb-2 flex justify-center sm:mb-3">
        <div className="flex items-center gap-1 rounded-full bg-gradient-to-r from-red-600 to-pink-600 px-2 py-0.5 text-[10px] text-white sm:gap-1.5 sm:px-3 sm:py-1 sm:text-xs">
          <GenderIcon gender={config.gender} className="h-3.5 w-3.5" />
          <span>{t(`filters.${config.gender}`)}</span>
        </div>
      </div>

      {isFurry ? (
        <>
          {/* Species Grid for Furry */}
          <div className="mb-2 sm:mb-3">
            <h3 className="mb-1.5 text-center text-xs font-semibold text-white sm:mb-2 sm:text-sm">
              <Trans i18nKey="common:characterCreator.secSpecies" />
            </h3>
            <div className="mx-auto grid max-w-[360px] grid-cols-6 gap-1.5 sm:max-w-lg sm:gap-2 md:max-w-2xl md:gap-3 lg:max-w-3xl">
              {furrySpeciesIds.map((id) => (
                <SpeciesCard
                  key={id}
                  speciesId={id}
                  label={t(`characterCreator.options.furrySpecies.${id}`)}
                  isSelected={config.species === id}
                  gender={config.gender}
                  onClick={() => updateConfig({ species: id })}
                />
              ))}
            </div>
          </div>

          {/* Fur Color + Age — boxed panel (same as the realism step) */}
          <div
            className="mx-auto mb-2 max-w-full rounded-2xl p-3 sm:mb-3 sm:max-w-2xl sm:p-4"
            style={{
              border: '1px solid rgba(236,72,153,0.45)',
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            <div className="mb-2 sm:mb-3">
              <h3 className="mb-1.5 text-left text-xs font-semibold text-white sm:mb-2 sm:text-sm">
                <Trans i18nKey="common:characterCreator.secFurColor" />
              </h3>
              <div className="flex w-full flex-wrap items-start justify-between gap-y-2">
                {furColors.map((color) => (
                  <button
                    key={color.id}
                    onClick={() => updateConfig({ furColor: color.id })}
                    className="flex flex-col items-center gap-1"
                  >
                    <span
                      className={`h-8 w-8 rounded-full transition-all sm:h-9 sm:w-9 ${
                        config.furColor === color.id
                          ? 'scale-105 ring-2 ring-pink-500'
                          : 'ring-1 ring-white/20 hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.color }}
                    />
                    <span className="text-[9px] font-medium text-white sm:text-[11px]">
                      {t(`characterCreator.options.furColor.${color.id}`)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Age (inside the same boxed panel) */}
            <div>{ageSlider}</div>
          </div>
        </>
      ) : (
        <>
          {/* Ethnicity Grid */}
          <div className="mb-2 sm:mb-3">
            <div className="mx-auto grid max-w-[300px] grid-cols-3 gap-2 sm:max-w-sm sm:gap-3 md:max-w-lg md:gap-4 lg:max-w-xl">
              {ethnicities.map((id, index) => (
                <EthnicityCard
                  key={`${id}-${index}`}
                  ethnicityId={id}
                  label={t(`characterCreator.options.ethnicity.${id}`)}
                  isSelected={config.ethnicity === id}
                  style={config.style}
                  gender={config.gender}
                  onClick={() => updateConfig({ ethnicity: id })}
                />
              ))}
            </div>
          </div>

          {/* Skin Tone + Age — boxed panel (design: one bordered container) */}
          <div
            className="mx-auto mb-2 max-w-full rounded-2xl p-3 sm:mb-3 sm:max-w-2xl sm:p-4"
            style={{
              border: '1px solid rgba(236,72,153,0.45)',
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            <div className="mb-2 sm:mb-3">
              <h3 className="mb-1.5 text-left text-xs font-semibold text-white sm:mb-2 sm:text-sm">
                <Trans i18nKey="common:characterCreator.secSkinTone" />
              </h3>
              <div className="flex w-full flex-wrap items-start justify-between gap-y-2">
                {skinTones.map((tone) => {
                  const isSelected =
                    !config.customSkinTone && config.skinTone === tone.id;
                  return (
                    <button
                      key={tone.id}
                      onClick={() =>
                        updateConfig({
                          skinTone: tone.id,
                          customSkinTone: undefined,
                        })
                      }
                      className="flex flex-col items-center gap-1"
                    >
                      <span
                        className={`h-8 w-8 rounded-full transition-all sm:h-9 sm:w-9 ${
                          isSelected
                            ? 'scale-105 ring-2 ring-pink-500'
                            : 'ring-1 ring-white/20 hover:scale-105'
                        }`}
                        style={{ backgroundColor: tone.color }}
                      />
                      <span className="text-[9px] font-medium text-white sm:text-[11px]">
                        {t(`characterCreator.options.skinTone.${tone.id}`)}
                      </span>
                    </button>
                  );
                })}

                {/* Custom — opens a native colour picker */}
                <label className="flex cursor-pointer flex-col items-center gap-1">
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full transition-all sm:h-9 sm:w-9 ${
                      config.customSkinTone
                        ? 'scale-105 ring-2 ring-pink-500'
                        : 'ring-1 ring-white/25 hover:scale-105'
                    }`}
                    style={{
                      background:
                        config.customSkinTone ??
                        'conic-gradient(#f5e6d3,#c68642,#7a4f2e,#3d2817,#f5e6d3)',
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5 text-white drop-shadow" />
                  </span>
                  <span className="text-[9px] font-medium text-white sm:text-[11px]">
                    {t('characterCreator.options.skinTone.custom', {
                      defaultValue: 'Custom',
                    })}
                  </span>
                  <input
                    type="color"
                    className="sr-only"
                    value={config.customSkinTone ?? '#c68642'}
                    onChange={(e) =>
                      updateConfig({ customSkinTone: e.target.value })
                    }
                  />
                </label>
              </div>
            </div>

            {/* Age (inside the same boxed panel) */}
            <div>{ageSlider}</div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center gap-2 sm:gap-2.5">
        <button
          onClick={onBack}
          className="rounded-lg border border-white/30 bg-transparent px-4 py-1.5 text-[10px] font-semibold text-white transition-all hover:border-white/50 hover:bg-white/5 sm:border-2 sm:px-6 sm:py-2 sm:text-xs md:px-8"
        >
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>

        <button
          onClick={onNext}
          className="rounded-lg bg-gradient-to-r from-red-600 via-pink-600 to-pink-700 px-6 py-1.5 text-[10px] font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-red-700 hover:via-pink-700 hover:to-pink-800 sm:px-8 sm:py-2 sm:text-xs md:px-10"
        >
          <Trans i18nKey="common:characterCreator.nextButton" />
        </button>
      </div>
    </div>
  );
});

function SpeciesCard({
  speciesId,
  label,
  isSelected,
  gender,
  onClick,
}: {
  speciesId: string;
  label: string;
  isSelected: boolean;
  gender: Gender;
  onClick: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const imgSrc = getSpeciesImage(speciesId, gender);

  useEffect(() => {
    setImgError(false);
  }, [imgSrc]);

  return (
    <button
      onClick={onClick}
      className={`relative aspect-[3/4] cursor-pointer overflow-hidden rounded-md transition-all duration-300 ease-out sm:rounded-lg ${
        isSelected
          ? 'border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)] sm:shadow-[0_0_15px_rgba(236,72,153,0.5)]'
          : 'border border-white/10 hover:border-white/30'
      }`}
    >
      {!imgError ? (
        <img
          src={imgSrc}
          alt={label}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <span className="px-1 text-center text-[10px] font-medium text-gray-400 sm:text-xs">
            {label}
          </span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      <div className="absolute right-0 bottom-0 left-0 p-1 sm:p-1.5">
        <h3 className="text-center text-[8px] font-bold text-white sm:text-[10px] md:text-xs">
          {label}
        </h3>
      </div>
      {isSelected && (
        <div className="absolute top-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-pink-500 sm:top-1.5 sm:right-1.5 sm:h-4 sm:w-4">
          <Check className="h-2 w-2 text-white sm:h-2.5 sm:w-2.5" />
        </div>
      )}
    </button>
  );
}

function EthnicityCard({
  ethnicityId,
  label,
  isSelected,
  style,
  gender,
  onClick,
}: {
  ethnicityId: Ethnicity;
  label: string;
  isSelected: boolean;
  style: StyleType;
  gender: Gender;
  onClick: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const imgSrc = getEthnicityImage(ethnicityId, style, gender);

  useEffect(() => {
    setImgError(false);
  }, [imgSrc]);

  return (
    <button
      onClick={onClick}
      className={`relative aspect-[3/4] cursor-pointer overflow-hidden rounded-md transition-all duration-300 ease-out sm:rounded-lg ${
        isSelected
          ? 'border-2 border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)] sm:shadow-[0_0_15px_rgba(236,72,153,0.5)]'
          : 'border border-white/10 hover:border-white/30'
      }`}
    >
      {!imgError ? (
        <img
          src={imgSrc}
          alt={label}
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <span className="px-1 text-center text-[10px] font-medium text-gray-400 sm:text-xs">
            {label}
          </span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      <div className="absolute right-0 bottom-0 left-0 p-1 sm:p-1.5">
        <h3 className="text-center text-[8px] font-bold text-white sm:text-[10px] md:text-xs">
          {label}
        </h3>
      </div>
      {isSelected && (
        <div className="absolute top-1 right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-pink-500 sm:top-1.5 sm:right-1.5 sm:h-4 sm:w-4">
          <Check className="h-2 w-2 text-white sm:h-2.5 sm:w-2.5" />
        </div>
      )}
    </button>
  );
}
